import React from "react";
import { motion } from "framer-motion";
import { Target, Eye, Sparkles, Workflow, Brain, Award } from "lucide-react";
import { FinalCTA } from "@/components/Sections";

const VALUES = [
  { icon: Target, title: "Mission", desc: "Accelerate every ambitious candidate into a high-paying US career using AI and human expertise." },
  { icon: Eye, title: "Vision", desc: "Be the most trusted AI-powered placement partner for global talent entering the US market." },
  { icon: Sparkles, title: "Why Xyro", desc: "We're not a training institute. We're an outcome-driven placement engine engineered for the US." },
  { icon: Workflow, title: "Our Process", desc: "Assess → Optimize → Prepare → Place. A nine-step, AI-augmented journey to your offer." },
  { icon: Brain, title: "AI Ecosystem", desc: "Resume analyzer, LinkedIn optimizer, interview coach, job match and career roadmap." },
  { icon: Award, title: "Career Experts", desc: "Senior US recruiters, hiring managers and AI engineers backing your candidacy." },
];

export default function AboutPage() {
  return (
    <main className="pt-32 sm:pt-36 pb-10 overflow-hidden" data-testid="about-page">
      <div className="max-w-7xl mx-auto px-6">
        <div className="max-w-3xl">
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#9db1ff]">About Xyro</p>
          <h1 className="mt-4 font-heading text-5xl md:text-6xl font-bold text-white tracking-tighter leading-[1.05]">
            We don&apos;t teach. <br />
            <span className="text-gradient-electric">We place.</span>
          </h1>
          <p className="mt-5 text-zinc-400 text-lg leading-relaxed">
            Xyro Tech Solutions is an AI-powered US career acceleration partner. We combine intelligent
            tooling, senior US recruiters and a proven nine-step playbook to put global talent into
            high-paying roles across the United States.
          </p>
        </div>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {VALUES.map((v, i) => (
            <motion.div
              key={v.title}
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.45, delay: (i % 3) * 0.05 }}
              className="glass-prism p-7"
              data-testid={`about-value-${i}`}
            >
              <div className="w-11 h-11 rounded-xl bg-[#2B4DFF]/15 border border-[#2B4DFF]/30 flex items-center justify-center mb-5">
                <v.icon className="w-5 h-5 text-[#9db1ff]" />
              </div>
              <h3 className="font-heading text-xl text-white">{v.title}</h3>
              <p className="mt-2 text-zinc-400 text-sm leading-relaxed">{v.desc}</p>
            </motion.div>
          ))}
        </div>

        <div className="mt-20 glass-prism p-8 md:p-12">
          <div className="grid md:grid-cols-2 gap-10 items-center">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#9db1ff]">The Xyro promise</p>
              <h2 className="mt-3 font-heading text-3xl md:text-4xl text-white tracking-tighter leading-[1.1]">
                A premium placement experience, engineered for results.
              </h2>
              <p className="mt-5 text-zinc-400 leading-relaxed">
                From the first consultation to your signed offer, we treat your career like a high-stakes product launch — measured, optimized and relentlessly executed.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {[
                { v: "9", l: "Step roadmap" },
                { v: "5", l: "AI engine modules" },
                { v: "180+", l: "Partner companies" },
                { v: "24h", l: "Response SLA" },
              ].map((s) => (
                <div key={s.l} className="glass p-5">
                  <p className="font-heading text-3xl text-white">{s.v}</p>
                  <p className="text-xs uppercase tracking-wider text-zinc-500 mt-1">{s.l}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <FinalCTA />
    </main>
  );
}
